import { logger } from '@adornis/base/logging.js';
import {
  Check,
  ChevronRight,
  Copy,
  FileText,
  Loader2,
  MessageSquare,
  Pencil,
  Save,
  ThumbsDown,
  ThumbsUp,
  X,
} from 'lucide-react';
import React, { createRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Structure extraction is now triggered server-side in generate-answer; client token handling removed.
import { confirmSubmissionSplitMutation } from '../../../db/api/confirm-submission-split.js';
import { giveFeedbackMutation } from '../../../db/api/give-feedback.js';
import { getTaskTypeLabel, TaskType } from '../../../db/api/task-types/task-main.js';
import {
  AssignmentProposalMessage,
  AssistantMessage,
  ConfirmationMessage,
  UserMessage,
  type Message,
} from '../../../db/conversation.js';
import { SUPPORTED_LANGUAGES } from '../../../i18n.js';
import { useVisibilityRecovery } from '../../lib/useVisibilityRecovery.js';
import { cn } from '../../lib/utils.js';
import { MarkdownWithMath } from '../markdown/markdown-with-math.js';
import { Button } from '../ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog.js';
import { MessageScrollContainer } from '../ui/message-container.js';
import { Textarea } from '../ui/textarea.js';
import { ThinkingOwl } from '../ui/thinking-owl.js';
import {
  AssignmentConfirmationPanel,
  type AssignmentConfirmationPanelHandle,
} from './assignment-confirmation-panel.js';

interface ChatMessagesProps {
  messages: Message[];
  isThinking?: boolean;
  isAgentThinking?: boolean;
  agentThinkingText?: string;
  conversationId: string;
  conversationLanguage?: string | null;
  onEditMessage?: (messageId: string, newContent: string) => void;
  onSubmit: (message: string, opts?: { attachedFileNames?: string[] | null }) => void;
  isLoading?: boolean;
  examId?: string | null;
  onExtractionComplete?: (addedMessage?: any) => Promise<void>;
  onExamRefresh?: () => Promise<void>;
  onOpenCanvas?: (view: 'submissioncheck' | 'structureeditor') => void;
  panelRefsRef?: React.MutableRefObject<Record<string, React.RefObject<AssignmentConfirmationPanelHandle | null>>>;
  splitConfirmationState?: { status?: string; confirmed?: boolean; messageId?: string | null } | null;
  structureConfirmationState?: { status?: string; confirmed?: boolean; messageId?: string | null } | null;
  pendingMessage?: string | null;
}

function dedupeExactRepeatedAssistantContent(input: string): string {
  const source = input.trim();
  if (source.length < 80) return input;

  const center = Math.floor(source.length / 2);
  for (let offset = -24; offset <= 24; offset++) {
    const split = center + offset;
    if (split <= 0 || split >= source.length) continue;
    const left = source.slice(0, split).trimEnd();
    const right = source.slice(split).trimStart();
    if (left.length >= 40 && left === right) {
      return left;
    }
  }

  if (source.length % 2 === 0) {
    const half = source.length / 2;
    const left = source.slice(0, half);
    const right = source.slice(half);
    if (left.length >= 40 && left === right) {
      return left;
    }
  }

  return input;
}

type AssistantRenderSegment =
  | { type: 'text'; content: string }
  | { type: 'thinking'; label: string; content: string; isClosed: boolean };

const TOOL_LOG_TAG_REGEX = /<tool-log\s+tool="([^"]+)"\s+step="([^"]+)">/i;
const TOOL_LOG_TAG_SUFFIX = '</tool-log>';

const normalizeThinkingLabel = (value: string): string => value.replace(/"$/, '').trim();

function parseAssistantRenderSegments(input: string): AssistantRenderSegment[] {
  const segments: AssistantRenderSegment[] = [];
  const raw = input || '';
  let cursor = 0;

  while (cursor < raw.length) {
    // Gezielte Suche nach dem Tool-Log Start (ignoriert normale '<' Zeichen im Fließtext)
    const openStart = raw.toLowerCase().indexOf('<tool-log', cursor);

    if (openStart === -1) {
      let tail = raw.slice(cursor);

      // ===== FAILSAFE: Unvollständige Präfixe am Ende abfangen =====
      // Falls der Stream genau in einem "<too" abbricht, blenden wir es aus,
      // bis das nächste Token klärt, ob es ein Tool-Log wird.
      const lastLessThan = tail.lastIndexOf('<');
      if (lastLessThan !== -1) {
        const trailingPart = tail.slice(lastLessThan).toLowerCase();
        if ('<tool-log'.startsWith(trailingPart)) {
          tail = tail.slice(0, lastLessThan);
        }
      }

      if (tail) segments.push({ type: 'text', content: tail });
      break;
    }

    // Falls vor dem <tool-log noch normaler Text stand, pushen wir ihn zuerst
    if (openStart > cursor) {
      const prefix = raw.slice(cursor, openStart);
      if (prefix) segments.push({ type: 'text', content: prefix });
    }

    const headerEnd = raw.indexOf('>', openStart);
    // Da das Backend das vollständige Eröffnungstag garantiert, ist headerEnd hier immer vorhanden.
    if (headerEnd === -1) {
      break;
    }

    // Das öffnende Tag ist vollständig da. Jetzt parsen wir das Label
    const fullHeader = raw.slice(openStart, headerEnd + 1);
    const tagMatch = fullHeader.match(TOOL_LOG_TAG_REGEX);
    const label = normalizeThinkingLabel(tagMatch?.[2] || 'Verarbeite Daten...');

    // Suchen nach dem schließenden Suffix
    const closeStart = raw.indexOf(TOOL_LOG_TAG_SUFFIX, headerEnd + 1);
    if (closeStart === -1) {
      // Der Inhalt innerhalb des Tags wird aktuell noch im Body gestreamt
      const openBody = raw.slice(headerEnd + 1);
      segments.push({ type: 'thinking', label, content: openBody, isClosed: false });
      break;
    }

    // Das Tag ist komplett geschlossen
    const closedBody = raw.slice(headerEnd + 1, closeStart);
    const afterClose = raw.slice(closeStart + TOOL_LOG_TAG_SUFFIX.length);
    const hasContentAfter = afterClose.trim().length > 0;

    segments.push({ type: 'thinking', label, content: closedBody, isClosed: hasContentAfter });
    cursor = closeStart + TOOL_LOG_TAG_SUFFIX.length;
  }

  return segments;
}

export function ChatMessages({
  messages,
  isThinking,
  isAgentThinking,
  agentThinkingText,
  conversationId,
  conversationLanguage,
  onEditMessage,
  onSubmit,
  examId,
  onExtractionComplete,
  onExamRefresh,
  onOpenCanvas,
  panelRefsRef: parentPanelRefsRef,
  splitConfirmationState,
  structureConfirmationState,
  pendingMessage,
  isLoading,
}: ChatMessagesProps) {
  const { t, i18n } = useTranslation();
  const conversationLanguageCode =
    SUPPORTED_LANGUAGES.find(lang => lang.value === String(conversationLanguage || '').trim())?.value ??
    SUPPORTED_LANGUAGES.find(lang => lang.label === String(conversationLanguage || '').trim())?.value ??
    i18n.language;
  const confirmationT = i18n.getFixedT(conversationLanguageCode);
  const [feedbackStates, setFeedbackStates] = useState<Record<string, 'up' | 'down' | null>>({});
  const [feedbackTexts, setFeedbackTexts] = useState<Record<string, string>>({});
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const [dialogOpen, setDialogOpen] = useState<Record<string, boolean>>({});
  const streamingKeyRef = useRef<string | null>(null);
  const lastAssistantRef = useRef<HTMLDivElement | null>(null);
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const localPanelRefsRef = useRef<Record<string, React.RefObject<AssignmentConfirmationPanelHandle | null>>>({});
  const panelRefsRef = parentPanelRefsRef || localPanelRefsRef;

  const confirmAssignmentText = t('chatMessages.submitAssignmentConfirmation', {
    defaultValue: 'Confirm task assignment',
  });
  const confirmStructureText = t('chatMessages.submitStructureConfirmation', {
    defaultValue: 'Confirm exam structure',
  });

  const getOrCreatePanelRef = (messageId: string) => {
    if (!panelRefsRef.current[messageId]) {
      panelRefsRef.current[messageId] = createRef<AssignmentConfirmationPanelHandle | null>();
    }
    return panelRefsRef.current[messageId] as React.RefObject<AssignmentConfirmationPanelHandle>;
  };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<string>('');

  const startEditing = (id: string, currentContent: string) => {
    setEditingId(id);
    setEditContent(currentContent);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
  };

  const saveEdit = (id: string) => {
    if (onEditMessage && editContent.trim() !== '') {
      onEditMessage(id, editContent);
      setEditingId(null);
    }
  };

  const getMessageId = (message: Message) => message._id ?? (message as unknown as { id?: string }).id ?? '';
  const lastAssistantMessage = messages
    .filter(
      m =>
        m._class === AssistantMessage._class ||
        m._class === AssignmentProposalMessage._class ||
        m._class === ConfirmationMessage._class,
    )
    .at(-1);
  const lastAssistantId = lastAssistantMessage ? getMessageId(lastAssistantMessage) : null;

  // Fix for window occlusion: When the window is hidden (e.g., user switches to another OS window),
  // WebSocket messages may be lost due to browser throttling. This hook detects when the window
  // regains focus and refetches both the conversation and exam state so we recover missed
  // Structure Agent messages even if the live subscription event was dropped.
  useVisibilityRecovery({
    shouldRecover: () => !!examId,
    onRecover: async () => {
      if (onExtractionComplete) {
        logger.info(`[Window Occlusion Recovery] Refreshing conversation messages after focus regain`);
        await onExtractionComplete();
      }

      if (onExamRefresh) {
        logger.info(`[Window Occlusion Recovery] Window regained focus - syncing exam state from server`);
        await onExamRefresh();
      }
    },
    recoveryDebounceMs: 1000,
  });

  // Only clear the streaming key when the agent stops thinking entirely.
  // This prevents intermediate subscription updates from wiping the placeholder key.
  useEffect(() => {
    if (!isThinking) {
      streamingKeyRef.current = null;
    }
  }, [isThinking]);

  useEffect(() => {
    const ids = messages.map(m => getMessageId(m)).filter((id): id is string => Boolean(id && id.trim().length > 0));
    const counts = ids.reduce<Record<string, number>>((acc, id) => {
      acc[id] = (acc[id] || 0) + 1;
      return acc;
    }, {});
    const duplicates = Object.entries(counts).filter(([, count]) => count > 1);
    if (duplicates.length > 0) {
      logger.warn(
        `Duplicate message IDs detected in ChatMessages for ${conversationId}: ${JSON.stringify(duplicates)}`,
      );
    }
  }, [messages, conversationId]);

  const handleRating = (messageId: string, rating: 'up' | 'down') => {
    setFeedbackStates(prev => ({
      ...prev,
      [messageId]: prev[messageId] === rating ? null : rating,
    }));
  };

  const handleCopy = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedStates(prev => ({ ...prev, [messageId]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [messageId]: false }));
      }, 2000);
    } catch (err) {
      logger.error(err, `Kopieren fehlgeschlagen:`);
    }
  };

  const handleFeedbackSubmit = (messageId: string, rating: 'up' | 'down') => {
    if (!messageId) {
      logger.warn(`Cannot submit feedback without a message id`);
      return;
    }

    const feedbackText = feedbackTexts[messageId] || '';

    void giveFeedbackMutation(conversationId, messageId, rating, feedbackText)()
      .catch(err => {
        logger.error(err, `Failed to submit feedback`);
      })
      .finally(() => {
        setDialogOpen(prev => ({ ...prev, [messageId]: false }));
        setFeedbackTexts(prev => ({ ...prev, [messageId]: '' }));
      });
  };

  const splitMessageId = splitConfirmationState?.messageId ?? '';
  const structureMessageId = structureConfirmationState?.messageId ?? '';
  const splitConfirmed = Boolean(splitConfirmationState?.confirmed || splitConfirmationState?.status === 'DONE');
  const structureConfirmed = Boolean(
    structureConfirmationState?.confirmed || structureConfirmationState?.status === 'DONE',
  );

  const handleConfirmSubmissionSplit = async (targetMessageId?: string) => {
    const confirmationMessageId = targetMessageId || splitMessageId;
    if (!examId || !confirmationMessageId) return;

    try {
      await confirmSubmissionSplitMutation(examId, conversationId, confirmationMessageId)();
      if (onExtractionComplete) {
        await onExtractionComplete();
      }
    } catch (err) {
      logger.error(err, `Failed to confirm submission split from button:`);
    }

    onSubmit(confirmAssignmentText);
  };

  const lastUserMessageId = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg && (msg._class === UserMessage._class || msg._class === 'UserMessage' || msg._class === 'user')) {
        return getMessageId(msg);
      }
    }
    return null;
  })();

  const lastNonUserMessageId = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (!msg) continue;
      const cls = msg._class;
      if (cls !== UserMessage._class && cls !== 'UserMessage' && cls !== 'user') {
        const id = getMessageId(msg);
        if (id && !id.startsWith('__placeholder__')) return id;
      }
    }
    return null;
  })();

  // Check if the very last message in the array is a user message
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const isLastMessageUser =
    lastMessage &&
    (lastMessage._class === UserMessage._class ||
      lastMessage._class === 'UserMessage' ||
      lastMessage._class === 'user');

  const isWaitingForAnswer = (isLoading || isThinking) && isLastMessageUser && !isAgentThinking;

  // Build a presentation list of messages that includes an invisible placeholder
  // assistant message when the user is the last sender and the agent is thinking.
  const placeholderId = `__placeholder__${lastUserMessageId ?? String(messages.length)}`;
  const displayMessages: Message[] = ((): Message[] => {
    if (isWaitingForAnswer) {
      const exists = messages.some(m => getMessageId(m) === placeholderId);
      if (exists) return messages;
      const placeholder: any = {
        _id: placeholderId,
        _class: AssistantMessage._class,
        content: '',
        __isPlaceholder: true,
      };
      return [...messages, placeholder];
    }
    return messages;
  })();

  // Ensure a stable streaming key when the agent is thinking after a user message.
  useEffect(() => {
    if (!(isThinking || isLoading)) return;
    if (!isLastMessageUser) return;
    if (streamingKeyRef.current) return;

    streamingKeyRef.current = `stream-user-${lastUserMessageId ?? `idx-${messages.length}`}`;
  }, [isThinking, isLoading, isLastMessageUser, lastUserMessageId, messages.length]);

  return (
    <MessageScrollContainer key={conversationId} latestUserMessageId={lastUserMessageId} latestNonUserMessageId={lastNonUserMessageId}>
      {messages.length === 0 ? (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          {t('chatMessages.emptyState')}
        </div>
      ) : (
        displayMessages.map((message, index) => {
          const messageId = getMessageId(message);
          const isAssistantMessage =
            message._class === AssistantMessage._class ||
            message._class === AssignmentProposalMessage._class ||
            message._class === ConfirmationMessage._class;
          const isLastMessageInArray = index === displayMessages.length - 1;
          const isThinkingMessage = Boolean(
            (message as any).__isPlaceholder === true ||
            ((isThinking || isLoading) && message === lastAssistantMessage && isLastMessageInArray),
          );
          if (isThinkingMessage) {
            if (!streamingKeyRef.current) {
              const nextStreamingBase = messageId || `idx-${index}`;
              streamingKeyRef.current = `stream-${nextStreamingBase}`;
            }
          }
          const messageKey = isThinkingMessage
            ? streamingKeyRef.current || `stream-idx-${index}`
            : messageId
              ? `msg-${messageId}`
              : `${message._class}-${index}`;
          const isLastAssistantMessage = isAssistantMessage && messageId === lastAssistantId;

          const content = message.content as any;
          const persistedConfirmed = Boolean((message as any).confirmed);
          const isConfirmationMessage = message._class === ConfirmationMessage._class;
          const confirmationType =
            typeof (message as any).confirmationType === 'string' ? (message as any).confirmationType : '';
          const isSubmissionSplitConfirmationMessage =
            messageId === splitMessageId || (isConfirmationMessage && confirmationType === 'submissionSplit');
          const isStructureConfirmationMessage =
            messageId === structureMessageId ||
            (isConfirmationMessage && (confirmationType === 'structure' || confirmationType === ''));
          const isAnyConfirmationMessage = isSubmissionSplitConfirmationMessage || isStructureConfirmationMessage;
          const isMessageConfirmed = isSubmissionSplitConfirmationMessage
            ? splitConfirmed || persistedConfirmed
            : isStructureConfirmationMessage
              ? structureConfirmed || persistedConfirmed
              : persistedConfirmed;

          const confirmationLabel = t('chatMessages.confirmed');

          const rawContent = typeof message.content === 'string' ? message.content : String(message.content || '');
          let renderedMessageContent = rawContent.trim();

          // INTERCEPTOR FÜR SPLIT-CONFIRMATION
          if (renderedMessageContent.includes('[[SPLIT_CONFIRMATION]]')) {
            try {
              const jsonStr = renderedMessageContent
                .substring(renderedMessageContent.indexOf('[[SPLIT_CONFIRMATION]]') + 22)
                .trim();
              const payload = JSON.parse(jsonStr);
              let problemText = '';

              if (payload.problemPayload && payload.problemPayload.length > 2) {
                try {
                  const issues = JSON.parse(payload.problemPayload);
                  const lines = issues.map((iss: any) => {
                    const prefix =
                      iss.type === 'error'
                        ? confirmationT('splitIssues.errorPrefix')
                        : confirmationT('splitIssues.warningPrefix');
                    let resolvedNames = iss.args?.names || '';

                    resolvedNames = resolvedNames.replace(/__STUDENT__(\d+)/g, (_match: any, p1: any) =>
                      confirmationT('splitIssues.studentNumber', { number: p1 }),
                    );
                    resolvedNames = resolvedNames.replace(/__UNKNOWN__/g, confirmationT('splitIssues.unknownStudent'));

                    return `- ${prefix}: ${confirmationT(iss.key, { ...iss.args, names: resolvedNames })}`;
                  });
                  problemText = lines.join('\n');
                } catch (e) {
                  problemText = payload.problemPayload;
                }
              }

              const contentArgs = { count: payload.count, range: payload.range, problem: problemText };
              if (payload.issueMode === 'error') {
                renderedMessageContent = confirmationT('splitIssues.messageError', contentArgs);
              } else if (payload.issueMode === 'warning') {
                renderedMessageContent = confirmationT('splitIssues.messageWarning', contentArgs);
              } else {
                renderedMessageContent = confirmationT('splitIssues.messageSuccess', contentArgs);
              }
            } catch (e) {
              logger.error(e, `Failed to parse split confirmation payload`);
              const parseErrorFallback = confirmationT('splitIssues.parseErrorFallback');
              renderedMessageContent = confirmationT('splitIssues.messageWarning', {
                count: 0,
                range: '',
                problem: parseErrorFallback,
              });
            }
          }
          // INTERCEPTOR FÜR STRUCTURE-CONFIRMATION
          else if (renderedMessageContent.includes('[[STRUCTURE_CONFIRMATION]]')) {
            try {
              const jsonStr = renderedMessageContent
                .substring(renderedMessageContent.indexOf('[[STRUCTURE_CONFIRMATION]]') + 26)
                .trim();
              const payload = JSON.parse(jsonStr);

              const countKey =
                payload.subTaskCount > 0
                  ? payload.mainTaskCount === 1
                    ? 'structureIssues.mainAndSubTasks_one'
                    : 'structureIssues.mainAndSubTasks_other'
                  : payload.mainTaskCount === 1
                    ? 'structureIssues.mainTasks_one'
                    : 'structureIssues.mainTasks_other';

              const countText = confirmationT(countKey, {
                mainCount: payload.mainTaskCount,
                subCount: payload.subTaskCount,
              });

              let display = confirmationT('structureIssues.intro', { countText });

              if (payload.preview && payload.preview.length > 0) {
                const previewLines = payload.preview.map((p: any) => {
                  const indent = '  '.repeat(p.indent);
                  const label = p.isRoot ? `${confirmationT('structureIssues.task')} ${p.label}` : p.label;
                  const taskTypeLabel = p.taskType ? getTaskTypeLabel(p.taskType as TaskType, confirmationT) : '';
                  const typeStr = taskTypeLabel ? ` – ${taskTypeLabel}` : '';
                  const warningStr = (p.warningCodes || [])
                    .map((c: string) => confirmationT(`structureIssues.${c}`, { defaultValue: c }))
                    .join(', ');
                  return `${indent}- ${label}${typeStr}${warningStr ? ` (${warningStr})` : ''}`;
                });
                display += `\n\n${previewLines.join('\n')}`;
              }

              if (payload.warningLabels && payload.warningLabels.length > 0) {
                const translatedWarnings = payload.warningLabels
                  .map((c: string) => confirmationT(`structureIssues.${c}`, { defaultValue: c }))
                  .join(', ');
                display += confirmationT('structureIssues.problemsIntro', { warnings: translatedWarnings });
              } else if (payload.lowConfidenceTaskLabels && payload.lowConfidenceTaskLabels.length > 0) {
                const translatedLabels = payload.lowConfidenceTaskLabels
                  .map((l: string) => `${confirmationT('structureIssues.task')} ${l}`)
                  .join(', ');
                display += confirmationT('structureIssues.uncertaintyIntro', { labels: translatedLabels });
              } else {
                display += confirmationT('structureIssues.success');
              }

              // When the sheet had points, explain how points are handled (grading-scheme upload wins;
              // the canvas points only matter for later GS generation) and the assignment rules.
              if (payload.hasPoints) {
                display += confirmationT('structureIssues.pointsExplanation');
              }

              renderedMessageContent = display;
            } catch (e) {
              logger.error(e, `Failed to parse structure confirmation payload`);
              renderedMessageContent = t('structureIssues.success');
            }
          } else {
            if (isAssistantMessage) {
              renderedMessageContent = dedupeExactRepeatedAssistantContent(renderedMessageContent);
            }
            renderedMessageContent = renderedMessageContent.replace(/\[\[OPEN_CANVAS:[^\]]+\]\]/g, '').trim();
          }

          let assignmentProposal: any = null;
          if (message._class === AssignmentProposalMessage._class) {
            const assignmentMessage =
              typeof (message as any)?.toObject === 'function' ? (message as any).toObject() : (message as any);
            assignmentProposal = {
              schuelerabgaben: Array.isArray(assignmentMessage?.schuelerabgaben)
                ? assignmentMessage.schuelerabgaben
                : [],
              musterloesung: assignmentMessage?.musterloesung,
              bewertungsschema: assignmentMessage?.bewertungsschema,
              leereKlausur: assignmentMessage?.leereKlausur,
              allowBlankExam: assignmentMessage?.allowBlankExam,
              active: typeof assignmentMessage?.active === 'boolean' ? assignmentMessage.active : true,
            };
          }

          // --- HIER IST DEIN GEWÜNSCHTER FIX FÜR DAS BESTÄTIGTE PANEL ---
          if (assignmentProposal && assignmentProposal.active === false) {
            let base = String(renderedMessageContent || '').trim();
            if (!base) {
              base = t('chatMessages.confirmedAssignmentIntro', { defaultValue: 'Die bestätigte Dateizuordnung:' });
            }

            const lines: string[] = [];
            if (Array.isArray(assignmentProposal.schuelerabgaben) && assignmentProposal.schuelerabgaben.length > 0) {
              lines.push(
                `- **${t('chatMessages.studentSubmissions', { defaultValue: 'Schülerabgaben' })}:**\n  ${assignmentProposal.schuelerabgaben.join(', ')}`,
              );
            }
            if (assignmentProposal.musterloesung) {
              lines.push(
                `- **${t('chatMessages.masterSolution', { defaultValue: 'Musterlösung' })}:**\n  ${assignmentProposal.musterloesung}`,
              );
            }
            if (assignmentProposal.bewertungsschema) {
              lines.push(
                `- **${t('chatMessages.gradingScheme', { defaultValue: 'Bewertungsschema' })}:**\n  ${assignmentProposal.bewertungsschema}`,
              );
            }
            if (assignmentProposal.allowBlankExam !== false && assignmentProposal.leereKlausur) {
              lines.push(
                `- **${t('chatMessages.blankExam', { defaultValue: 'Leere Klausur' })}:**\n  ${assignmentProposal.leereKlausur}`,
              );
            }

            renderedMessageContent = lines.length > 0 ? `${base}\n\n${lines.join('\n')}` : base;
          } else if (assignmentProposal && assignmentProposal.active !== false) {
            let base = String(renderedMessageContent || '').trim();
            if (!base) {
              base = t('chatMessages.assignmentIntro', {
                defaultValue:
                  'Ich habe die Dateien wie folgt zugeordnet. Bitte überprüfe die Zuordnung und bestätige sie:',
              });
            }
            renderedMessageContent = base;
          }

          const isCanvasControlMessage =
            typeof content === 'string' && content.startsWith('[[OPEN_CANVAS:') && content.endsWith(']]');
          const isSystemInfoMessage = message._class === 'SystemInfoMessage';
          const assistantSegments = isAssistantMessage ? parseAssistantRenderSegments(renderedMessageContent) : [];

          const userContentStr =
            message._class === UserMessage._class
              ? typeof content === 'string'
                ? content
                : String(content ?? '')
              : '';
          const ATTACH_RE = /\[\[ATTACHMENT:([^\]]+)\]\]/g;
          ATTACH_RE.lastIndex = 0;
          const attachments: Array<{ name: string }> = [];
          const cleanedUserContent =
            message._class === UserMessage._class
              ? userContentStr
                  .replace(ATTACH_RE, (_match, rawToken) => {
                    const trimmed = String(rawToken).trim();
                    if (trimmed.length > 0) {
                      const separatorIndex = trimmed.indexOf(':');
                      if (separatorIndex > 0) {
                        const encodedName = trimmed.slice(separatorIndex + 1).trim();
                        let decodedName = encodedName;
                        try {
                          decodedName = encodedName ? decodeURIComponent(encodedName) : '';
                        } catch {
                          decodedName = encodedName;
                        }
                        attachments.push({ name: decodedName || encodedName || trimmed });
                      } else {
                        attachments.push({ name: trimmed });
                      }
                    }
                    return '';
                  })
                  .trim()
              : '';
          const hasUserAttachments = attachments.length > 0;
          const visibleAttachments = attachments.slice(0, 3);
          const hiddenAttachmentCount = Math.max(0, attachments.length - visibleAttachments.length);
          const canEditInsideBubble =
            message._class === UserMessage._class && messageId === lastUserMessageId && Boolean(onEditMessage);

          if (isCanvasControlMessage || isSystemInfoMessage) {
            return null;
          }

          return (
            <div
              key={messageKey}
              data-message-id={messageId}
              ref={el => {
                if (messageId === lastUserMessageId) {
                  lastUserMessageRef.current = el;
                }

                if (isLastAssistantMessage) {
                  lastAssistantRef.current = el;
                }
              }}
              className={cn('flex gap-3', message._class === UserMessage._class && 'justify-end')}
            >
              {isAssistantMessage && (
                <div className="relative flex-shrink-0">
                  <div className="h-8 w-8 flex items-center justify-center">
                    <ThinkingOwl mode={isThinkingMessage ? 'thinking' : 'complete'} />
                  </div>
                </div>
              )}

              <div
                className={cn(
                  'flex flex-col gap-2 w-full',
                  message._class === UserMessage._class ? 'max-w-xs items-end' : 'max-w-full',
                )}
              >
                {editingId === messageId ? (
                  <div className="w-full min-w-[300px] flex flex-col gap-2 bg-background border rounded-lg p-2 shadow-sm">
                    <Textarea
                      value={editContent}
                      onChange={e => setEditContent(e.target.value)}
                      className="min-h-[80px] resize-none border-0 focus-visible:ring-0 p-0"
                    />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={cancelEditing} className="h-7 px-2">
                        <X className="h-4 w-4 mr-1" /> {t('chatMessages.cancel')}
                      </Button>
                      <Button size="sm" onClick={() => saveEdit(messageId)} className="h-7 px-2">
                        <Save className="h-4 w-4 mr-1" /> {t('chatMessages.send')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div
                      className={cn(
                        'relative break-words',
                        (message as any).__isPlaceholder && 'opacity-0',
                        message._class === UserMessage._class
                          ? 'px-4 py-2 rounded-lg rounded-br-none bg-primary text-primary-foreground'
                          : 'text-foreground prose prose-sm max-w-none dark:prose-invert',
                        canEditInsideBubble && 'pr-11 pb-8',
                      )}
                    >
                      {message._class === UserMessage._class ? (
                        hasUserAttachments ? (
                          <div className="flex flex-col gap-2.5">
                            {cleanedUserContent && (
                              <p className="text-sm whitespace-pre-wrap break-words text-slate-100">
                                {cleanedUserContent}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-2">
                              {visibleAttachments.map((att, idx) => (
                                <button
                                  key={`${att.name}-${idx}`}
                                  type="button"
                                  className="group inline-flex max-w-[220px] items-center gap-2 rounded-full border border-slate-600/80 bg-slate-800/90 px-2.5 py-1.5 text-left transition-colors hover:bg-slate-700/95 hover:border-slate-500"
                                  aria-label={`Angehängte Datei ${att.name}`}
                                  title={att.name}
                                >
                                  <FileText className="h-4 w-4 shrink-0 text-slate-300 transition-colors group-hover:text-white" />
                                  <span className="text-xs font-medium text-slate-100/95 truncate">{att.name}</span>
                                </button>
                              ))}
                              {hiddenAttachmentCount > 0 && (
                                <span
                                  className="inline-flex items-center rounded-full border border-slate-600/80 bg-slate-800/90 px-2.5 py-1.5 text-xs font-medium text-slate-100/95"
                                  title={attachments
                                    .slice(3)
                                    .map(att => att.name)
                                    .join(', ')}
                                >
                                  +{hiddenAttachmentCount} weitere
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap break-words">{userContentStr}</p>
                        )
                      ) : (
                        <div className="space-y-2">
                          {assistantSegments.map((segment, segmentIndex) => {
                            if (segment.type === 'thinking') {
                              const label =
                                segment.label || t('chatMessages.toolProgress', { defaultValue: 'Thinking' });
                              return (
                                <details
                                  key={`thinking-segment-${messageKey}-${segmentIndex}`}
                                  open={!segment.isClosed}
                                  className="group"
                                >
                                  <summary className="cursor-pointer list-none select-none text-xs text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1.5">
                                    <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                                    {label}
                                  </summary>
                                  <div className="pl-4 mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    <MarkdownWithMath
                                      className="markdown-body"
                                      components={{
                                        p: ({ node, ...props }) => (
                                          <p
                                            className="mb-1 last:mb-0 leading-normal whitespace-pre-wrap break-words text-slate-500 dark:text-slate-400"
                                            {...props}
                                          />
                                        ),
                                      }}
                                    >
                                      {segment.content}
                                    </MarkdownWithMath>
                                  </div>
                                </details>
                              );
                            }

                            return (
                              <MarkdownWithMath
                                key={`text-segment-${messageKey}-${segmentIndex}`}
                                className="text-sm markdown-body"
                                components={{
                                  p: ({ node, ...props }) => (
                                    <p
                                      className="mb-2 last:mb-0 leading-normal whitespace-pre-wrap break-words"
                                      {...props}
                                    />
                                  ),
                                  table: ({ node, ...props }) => (
                                    <table className="border-collapse border border-slate-300 dark:border-slate-600 w-full my-2" {...props} />
                                  ),
                                  thead: ({ node, ...props }) => <thead className="bg-slate-100 dark:bg-slate-700" {...props} />,
                                  th: ({ node, ...props }) => (
                                    <th
                                      className="border border-slate-300 dark:border-slate-600 px-4 py-2 text-left font-semibold"
                                      {...props}
                                    />
                                  ),
                                  td: ({ node, ...props }) => (
                                    <td className="border border-slate-300 dark:border-slate-600 px-4 py-2" {...props} />
                                  ),
                                  ul: ({ node, ...props }) => <ul className="list-disc ml-4 mb-2" {...props} />,
                                  ol: ({ node, ...props }) => <ol className="list-decimal ml-4 mb-2" {...props} />,
                                  li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                                  strong: ({ node, ...props }) => (
                                    <strong className="font-semibold text-slate-900 dark:text-slate-100" {...props} />
                                  ),
                                  h1: ({ node, ...props }) => <h1 className="text-xl font-bold mb-2 mt-4" {...props} />,
                                  h2: ({ node, ...props }) => <h2 className="text-lg font-bold mb-2 mt-3" {...props} />,
                                  h3: ({ node, ...props }) => <h3 className="text-md font-bold mb-1 mt-2" {...props} />,
                                  a: ({ node, ...props }) => (
                                    <a
                                      className="text-brand-text hover:underline"
                                      target="_blank"
                                      rel="noreferrer"
                                      {...props}
                                    />
                                  ),
                                  code: ({ node, inline, ...props }: any) =>
                                    inline ? (
                                      <code
                                        className="bg-slate-200 dark:bg-slate-700 text-red-600 dark:text-red-400 px-1 py-0.5 rounded text-xs font-mono"
                                        {...props}
                                      />
                                    ) : (
                                      <pre className="bg-slate-800 text-slate-100 p-3 rounded-md overflow-x-auto text-xs font-mono my-2">
                                        <code {...props} />
                                      </pre>
                                    ),
                                }}
                              >
                                {segment.content}
                              </MarkdownWithMath>
                            );
                          })}
                        </div>
                      )}

                      {assignmentProposal && assignmentProposal.active !== false && (
                        <AssignmentConfirmationPanel
                          ref={getOrCreatePanelRef(messageId)}
                          baseContent={renderedMessageContent}
                          proposal={assignmentProposal}
                          allowBlankExam={assignmentProposal.allowBlankExam}
                          locked={Boolean(assignmentProposal.active === false)}
                          conversationId={conversationId}
                          messageId={messageId}
                        />
                      )}

                      {canEditInsideBubble && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="absolute bottom-1.5 right-1.5 h-7 w-7 rounded-md text-slate-200 dark:text-slate-300 hover:text-white hover:bg-white/10"
                          onClick={() => startEditing(messageId, message.content || '')}
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="sr-only">{t('chatMessages.edit')}</span>
                        </Button>
                      )}
                    </div>
                  </>
                )}

                {isAssistantMessage && !isThinkingMessage && (
                  <div className="flex items-center gap-1">
                    {isStructureConfirmationMessage ? (
                      <>
                        {!isMessageConfirmed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs hover:text-blue-500"
                            onClick={() => onOpenCanvas?.('structureeditor')}
                            disabled={!examId}
                          >
                            <Pencil className="h-4 w-4 text-blue-500 mr-1" />
                            {t('chatMessages.editInCanvas')}
                          </Button>
                        )}

                        {isMessageConfirmed ? (
                          <div className="h-7 px-2 text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 rounded-md inline-flex items-center">
                            <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
                            <span className="ml-1 text-xs">{confirmationLabel}</span>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={cn('h-7 px-2 hover:text-green-500')}
                            disabled={!examId}
                            // Just send the confirmation message; the agent calls confirm_structure_in_chat,
                            // which runs the point-consistency check and either confirms or blocks and
                            // re-opens the structure canvas.
                            onClick={() => onSubmit(confirmStructureText)}
                          >
                            <Check className="h-4 w-4 text-green-500" />
                            <span className="ml-1 text-xs">{t('chatMessages.confirm')}</span>
                          </Button>
                        )}
                      </>
                    ) : isSubmissionSplitConfirmationMessage ? (
                      <>
                        {!isMessageConfirmed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs hover:text-blue-500"
                            onClick={() => onOpenCanvas?.('submissioncheck')}
                            disabled={!examId}
                          >
                            <Pencil className="h-4 w-4 text-blue-500 mr-1" />
                            {t('chatMessages.editInCanvas')}
                          </Button>
                        )}

                        {isMessageConfirmed ? (
                          <div className="h-7 px-2 text-green-700 bg-green-100 rounded-md inline-flex items-center">
                            <Check className="h-4 w-4 text-green-500" />
                            <span className="ml-1 text-xs whitespace-nowrap">{confirmationLabel}</span>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 hover:text-green-500"
                            disabled={!examId}
                            onClick={() => {
                              void handleConfirmSubmissionSplit(messageId);
                            }}
                          >
                            <Check className="h-4 w-4 text-green-500" />
                            <span className="ml-1 text-xs whitespace-nowrap">
                              {t('chatMessages.confirmAssignment')}
                            </span>
                          </Button>
                        )}
                      </>
                    ) : isAnyConfirmationMessage ? null : (
                      <>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="h-7 w-7"
                          onClick={() => handleRating(messageId, 'up')}
                        >
                          <ThumbsUp className={cn('h-4 w-4', feedbackStates[messageId] === 'up' && 'fill-current')} />
                        </Button>

                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="h-7 w-7"
                          onClick={() => handleRating(messageId, 'down')}
                        >
                          <ThumbsDown
                            className={cn('h-4 w-4', feedbackStates[messageId] === 'down' && 'fill-current')}
                          />
                        </Button>

                        {feedbackStates[messageId] && (
                          <Dialog
                            open={dialogOpen[messageId]}
                            onOpenChange={open => setDialogOpen(prev => ({ ...prev, [messageId]: open }))}
                          >
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                                <MessageSquare className="h-3 w-3 mr-1" />
                                {t('chatMessages.giveFeedback')}
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[425px]">
                              <DialogHeader>
                                <DialogTitle>{t('chatMessages.feedbackTitle')}</DialogTitle>
                                <DialogDescription>{t('chatMessages.feedbackDescription')}</DialogDescription>
                              </DialogHeader>
                              <div className="grid gap-4 py-4">
                                <Textarea
                                  placeholder={t('chatMessages.feedbackPlaceholder')}
                                  value={feedbackTexts[messageId] || ''}
                                  onChange={e =>
                                    setFeedbackTexts(prev => ({
                                      ...prev,
                                      [messageId]: e.target.value,
                                    }))
                                  }
                                  className="min-h-[100px]"
                                />
                              </div>
                              <DialogFooter>
                                <Button
                                  className="bg-[brand-200] hover:bg-[brand-300] text-white"
                                  type="submit"
                                  onClick={() => handleFeedbackSubmit(messageId, feedbackStates[messageId]!)}
                                >
                                  {t('chatMessages.feedbackSubmit')}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        )}

                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className={cn('h-7 w-7', copiedStates[messageId] && 'text-blue-500')}
                          onClick={() =>
                            handleCopy(
                              typeof renderedMessageContent === 'string'
                                ? renderedMessageContent
                                : String(message.content || ''),
                              messageId,
                            )
                          }
                        >
                          {copiedStates[messageId] ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
      {isAgentThinking && (
        <div className="flex gap-3">
          <div className="relative flex-shrink-0">
            <div className="h-8 w-8 flex items-center justify-center">
              <ThinkingOwl mode="thinking" />
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full">
            <div className="relative text-foreground text-sm">
              <style>{`
                @keyframes agentThinkingDot {
                  0%, 60%, 100% { opacity: 0.2; }
                  30% { opacity: 1; }
                }
              `}</style>
              <span>{agentThinkingText}</span>
              <span aria-hidden="true">
                <span style={{ animation: 'agentThinkingDot 1.4s ease-in-out infinite', animationDelay: '0s' }}>.</span>
                <span style={{ animation: 'agentThinkingDot 1.4s ease-in-out infinite', animationDelay: '0.2s' }}>.</span>
                <span style={{ animation: 'agentThinkingDot 1.4s ease-in-out infinite', animationDelay: '0.4s' }}>.</span>
              </span>
            </div>
          </div>
        </div>
      )}
      {pendingMessage && (
        <div className="flex gap-3 justify-end">
          <div className="flex flex-col gap-2 w-full max-w-xs items-end">
            <div className="relative px-4 py-2 rounded-lg break-words bg-brand-700 dark:bg-brand-800 text-white rounded-br-none">
              <div className="flex flex-col gap-2.5">
                <p className="text-sm whitespace-pre-wrap break-words text-white">{pendingMessage}</p>
                <div className="flex items-center gap-1.5 text-blue-200">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-[11px]">Dateien werden hochgeladen…</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </MessageScrollContainer>
  );
}
